<script setup lang="ts">
import { marked, type Tokens } from 'marked';
import { computed, h, type VNode, type VNodeChild } from 'vue';
import { logger } from '@/utils/logger';

/**
 * A per-fenced-code-block action (e.g. Copy / Insert). When provided, each code
 * block renders these buttons in its corner; the handler receives the block's
 * raw text. Opt-in — omit it and code blocks render plain as before.
 */
export interface MarkdownCodeAction {
  key: string;
  label: string;
  handler: (code: string) => void;
}

interface Props {
  /** Markdown content to render */
  content: string;
  /** Maximum number of lines to show (for truncation). 0 or undefined = no truncation */
  maxLines?: number;
  /** Whether to show a fade overlay when truncated */
  showFadeOverlay?: boolean;
  /** Per-code-block action buttons (e.g. copy/insert). */
  codeActions?: MarkdownCodeAction[];
  /**
   * Takes over a link before the URL sanitizer sees it — the chat renders its
   * `gaia://` references as chips this way. Return null to fall through to the
   * normal (sanitized) link. `text` is the link's raw label, for callers that
   * need it as a string (to shorten it, say).
   */
  renderLink?: (href: string, children: VNodeChild[], text: string) => VNodeChild | null;
}

const props = withDefaults(defineProps<Props>(), {
  maxLines: 0,
  showFadeOverlay: false,
  codeActions: undefined,
  renderLink: undefined,
});

// Configure marked
marked.setOptions({
  breaks: true,
  gfm: true,
});

/**
 * Basic URL sanitization to avoid javascript: etc.
 * - Links: allow http(s), mailto, tel, and relative/hash.
 * - Images: allow http(s), relative, and data:image/*
 */
const LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);
const IMG_PROTOCOLS = new Set(['http:', 'https:']);
const SCHEME_RE = /^[a-z][a-z0-9+.-]*:/i;

function safeHref(raw?: string | null): string | undefined {
  if (!raw) return undefined;
  const v = raw.trim();
  if (!v) return undefined;

  // relative or hash
  if (v.startsWith('#') || v.startsWith('/') || v.startsWith('./') || v.startsWith('../')) return v;

  // "foo/bar" (no scheme) => treat as relative
  if (!SCHEME_RE.test(v)) return v;

  try {
    const u = new URL(v);
    return LINK_PROTOCOLS.has(u.protocol) ? u.toString() : undefined;
  } catch {
    return undefined;
  }
}

function safeImgSrc(raw?: string | null): string | undefined {
  if (!raw) return undefined;
  const v = raw.trim();
  if (!v) return undefined;

  if (v.startsWith('data:image/')) return v;
  if (v.startsWith('/') || v.startsWith('./') || v.startsWith('../')) return v;
  if (!SCHEME_RE.test(v)) return v;

  try {
    const u = new URL(v);
    return IMG_PROTOCOLS.has(u.protocol) ? u.toString() : undefined;
  } catch {
    return undefined;
  }
}

function hasMeaningfulInline(children: VNodeChild[]): boolean {
  return children.some(c => {
    if (typeof c === 'string') return c.trim().length > 0;
    // any VNode is meaningful
    return c != null;
  });
}

/**
 * Render inline tokens (text, strong, em, links, etc.)
 */
function renderInlineTokens(tokens: Tokens.Generic[]): VNodeChild[] {
  const nodes: VNodeChild[] = [];

  for (const token of tokens) {
    switch (token.type) {
      case 'text': {
        const t = token as Tokens.Text;
        // IMPORTANT: marked sometimes stores parsed inline children under text.tokens
        const textWithTokens = t as Tokens.Text & { tokens?: Tokens.Generic[] };
        if (Array.isArray(textWithTokens.tokens) && textWithTokens.tokens.length > 0) {
          nodes.push(...renderInlineTokens(textWithTokens.tokens));
        } else {
          nodes.push(t.text);
        }
        break;
      }

      case 'strong': {
        const t = token as Tokens.Strong;
        nodes.push(h('strong', { class: 'markdown-strong' }, renderInlineTokens(t.tokens)));
        break;
      }

      case 'em': {
        const t = token as Tokens.Em;
        nodes.push(h('em', { class: 'markdown-em' }, renderInlineTokens(t.tokens)));
        break;
      }

      case 'codespan': {
        const t = token as Tokens.Codespan;
        nodes.push(h('code', { class: 'markdown-inline-code' }, t.text));
        break;
      }

      case 'link': {
        const t = token as Tokens.Link;
        const childInline = t.tokens.length > 0 ? renderInlineTokens(t.tokens) : [t.text];
        const custom = props.renderLink?.(t.href, childInline, t.text);
        if (custom != null) {
          nodes.push(custom);
          break;
        }
        const href = safeHref(t.href);

        // If unsafe href, render as plain text span (non-clickable)
        if (!href) {
          nodes.push(h('span', { class: 'markdown-link markdown-link--unsafe' }, childInline));
          break;
        }

        const isExternal = /^https?:\/\//i.test(href);
        nodes.push(
          h(
            'a',
            {
              class: 'markdown-link',
              href,
              target: isExternal ? '_blank' : undefined,
              rel: isExternal ? 'noopener noreferrer' : undefined,
            },
            childInline
          )
        );
        break;
      }

      case 'image': {
        const t = token as Tokens.Image;
        const src = safeImgSrc(t.href);
        if (!src) {
          // render alt text if src is unsafe
          nodes.push(h('span', { class: 'markdown-image markdown-image--unsafe' }, t.text || ''));
          break;
        }
        nodes.push(
          h('img', {
            class: 'markdown-image',
            src,
            alt: t.text || '',
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to undefined
            title: t.title || undefined,
            loading: 'lazy',
          })
        );
        break;
      }

      case 'br': {
        nodes.push(h('br'));
        break;
      }

      case 'del': {
        const t = token as Tokens.Del;
        nodes.push(h('del', { class: 'markdown-del' }, renderInlineTokens(t.tokens)));
        break;
      }

      case 'html': {
        // Skip raw HTML for security
        const t = token as Tokens.HTML;
        nodes.push(t.text);
        break;
      }

      default: {
        // Best-effort fallback
        const unknownTok = token as unknown;
        if (
          typeof unknownTok === 'object' &&
          unknownTok !== null &&
          'tokens' in unknownTok &&
          Array.isArray(unknownTok.tokens) &&
          unknownTok.tokens.length > 0
        ) {
          nodes.push(...renderInlineTokens(unknownTok.tokens as Tokens.Generic[]));
        } else if (
          typeof unknownTok === 'object' &&
          unknownTok !== null &&
          'text' in unknownTok &&
          typeof unknownTok.text === 'string'
        ) {
          nodes.push(unknownTok.text);
        }
        break;
      }
    }
  }

  return nodes;
}

/**
 * Safely render markdown tokens as Vue VNodes
 */
function renderTokens(tokens: Tokens.Generic[]): VNode[] {
  const nodes: VNode[] = [];

  for (const token of tokens) {
    switch (token.type) {
      case 'heading': {
        const t = token as Tokens.Heading;
        const level = t.depth;
        const Tag = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
        const inline = renderInlineTokens(t.tokens);
        if (hasMeaningfulInline(inline)) {
          nodes.push(h(Tag, { class: 'markdown-heading' }, inline));
        }
        break;
      }

      case 'paragraph': {
        const t = token as Tokens.Paragraph;
        const inline = renderInlineTokens(t.tokens);
        if (hasMeaningfulInline(inline)) {
          nodes.push(h('p', { class: 'markdown-paragraph' }, inline));
        }
        break;
      }

      // ✅ CRITICAL FIX: block-level text tokens (very common inside list items)
      case 'text': {
        const t = token as Tokens.Text;
        const textWithTokens = t as Tokens.Text & { tokens?: Tokens.Generic[] };
        const inline =
          Array.isArray(textWithTokens.tokens) && textWithTokens.tokens.length > 0
            ? renderInlineTokens(textWithTokens.tokens)
            : [t.text];
        if (hasMeaningfulInline(inline)) {
          nodes.push(h('span', { class: 'markdown-text' }, inline));
        }
        break;
      }

      case 'list': {
        const t = token as Tokens.List;
        const Tag = t.ordered ? 'ol' : 'ul';

        const listProps: Record<string, unknown> = { class: 'markdown-list' };
        const listWithStart = t as Tokens.List & { start?: number };
        if (t.ordered && typeof listWithStart.start === 'number' && listWithStart.start !== 1) {
          listProps.start = listWithStart.start;
        }

        const listItems = t.items
          .map(item => {
            const liChildren: VNodeChild[] = [];

            // Task list support (harmless if unused)
            const taskItem = item as Tokens.ListItem & { task?: boolean; checked?: boolean };
            if (taskItem.task) {
              liChildren.push(
                h('input', {
                  class: 'markdown-task-checkbox',
                  type: 'checkbox',
                  disabled: true,
                  checked: Boolean(taskItem.checked),
                })
              );
            }

            const itemTokens: Tokens.Generic[] = item.tokens;
            const rendered = itemTokens.length > 0 ? renderTokens(itemTokens) : [];
            if (rendered.length > 0) liChildren.push(...rendered);

            // Fallback: if marked gave no tokens but text exists
            if (liChildren.length === 0 && typeof item.text === 'string' && item.text.trim()) {
              liChildren.push(h('span', { class: 'markdown-text' }, item.text));
            }

            if (liChildren.length === 0) return null;
            return h('li', { class: 'markdown-list-item' }, liChildren);
          })
          .filter((x): x is VNode => x !== null);

        if (listItems.length > 0) {
          nodes.push(h(Tag, listProps, listItems));
        }
        break;
      }

      case 'code': {
        // marked "code" is ALWAYS a block token
        const t = token as Tokens.Code;
        const codeNode = h('pre', { class: 'markdown-code-block' }, [
          h('code', { class: t.lang ? `language-${t.lang}` : undefined }, t.text),
        ]);
        const actions = props.codeActions ?? [];
        if (actions.length === 0) {
          nodes.push(codeNode);
        } else {
          // Wrap so the action buttons sit in the block's corner; each handler
          // gets this block's raw text (so multi-block replies copy per block).
          nodes.push(
            h('div', { class: 'markdown-code-wrap' }, [
              h(
                'div',
                { class: 'markdown-code-actions' },
                actions.map(action =>
                  h(
                    'button',
                    {
                      class: 'markdown-code-action',
                      type: 'button',
                      title: action.label,
                      onClick: () => {
                        action.handler(t.text);
                      },
                    },
                    action.label
                  )
                )
              ),
              codeNode,
            ])
          );
        }
        break;
      }

      case 'blockquote': {
        const t = token as Tokens.Blockquote;
        nodes.push(h('blockquote', { class: 'markdown-blockquote' }, renderTokens(t.tokens)));
        break;
      }

      case 'table': {
        const t = token as Tokens.Table;

        const headerRow = h(
          'tr',
          {},
          t.header.map(cell => {
            const cellWithTokens = cell as { tokens?: Tokens.Generic[] };
            return h(
              'th',
              { class: 'markdown-table-header' },
              renderInlineTokens(cellWithTokens.tokens ?? [])
            );
          })
        );

        const bodyRows = t.rows.map(row =>
          h(
            'tr',
            {},
            row.map(cell => {
              const cellWithTokens = cell as { tokens?: Tokens.Generic[] };
              return h(
                'td',
                { class: 'markdown-table-cell' },
                renderInlineTokens(cellWithTokens.tokens ?? [])
              );
            })
          )
        );

        nodes.push(
          h('table', { class: 'markdown-table' }, [
            h('thead', {}, [headerRow]),
            h('tbody', {}, bodyRows),
          ])
        );
        break;
      }

      case 'hr': {
        nodes.push(h('hr', { class: 'markdown-hr' }));
        break;
      }

      case 'html': {
        // Skip raw HTML for security - render as plain text
        const t = token as Tokens.HTML;
        if (t.text.trim()) nodes.push(h('span', { class: 'markdown-html-skipped' }, t.text));
        break;
      }

      case 'space': {
        // ignore
        break;
      }

      default: {
        // Best-effort fallback for unknown block tokens that still contain nested tokens
        const unknownTok = token as unknown;
        if (
          typeof unknownTok === 'object' &&
          unknownTok !== null &&
          'tokens' in unknownTok &&
          Array.isArray(unknownTok.tokens) &&
          unknownTok.tokens.length > 0
        ) {
          nodes.push(...renderTokens(unknownTok.tokens as Tokens.Generic[]));
        }
        break;
      }
    }
  }

  return nodes;
}

function countLines(node: VNode): number {
  const t = node.type;
  if (typeof t === 'string' && (/^h[1-6]$/.test(t) || t === 'p')) return 1;

  if (t === 'ul' || t === 'ol') {
    const children = node.children;
    if (Array.isArray(children)) {
      const listItems = children.filter((c): c is VNode => {
        return c != null && typeof c === 'object' && 'type' in c && c.type === 'li';
      });
      // Cap list items at maxLines to prevent over-truncation
      // This ensures at least some content is shown even with large lists
      return Math.min(listItems.length, 10) || 1;
    }
    return 1;
  }

  // treat other block nodes (table, blockquote, pre) as 1 "line" for truncation purposes
  return 1;
}

/**
 * Truncate rendered nodes to a maximum number of "lines" (block units).
 * Fixes false-positive truncation when content length == maxLines.
 * Ensures content is visible by showing at least some content even if it slightly exceeds maxLines.
 */
function truncateNodes(nodes: VNode[], maxLines: number): { nodes: VNode[]; truncated: boolean } {
  if (maxLines <= 0) return { nodes, truncated: false };
  if (nodes.length === 0) return { nodes: [], truncated: false };

  let used = 0;
  const out: VNode[] = [];

  for (const node of nodes) {
    const needed = countLines(node);

    // If adding this node would exceed maxLines
    if (used + needed > maxLines) {
      // Special handling: if we have no content yet (or only headings),
      // allow at least one content node to be shown even if it exceeds maxLines slightly
      // This prevents showing only headings with no actual content
      const hasOnlyHeadings = out.every(n => typeof n.type === 'string' && /^h[1-6]$/.test(n.type));

      if (hasOnlyHeadings && (node.type === 'ul' || node.type === 'ol' || node.type === 'p')) {
        // Allow showing at least one content node (list or paragraph) even if it exceeds maxLines
        // This ensures users see actual content, not just section headings
        out.push(node);
        return { nodes: out, truncated: true };
      }

      // Otherwise, stop truncation here
      return { nodes: out, truncated: true };
    }

    out.push(node);
    used += needed;
  }

  return { nodes: out, truncated: false };
}

const renderedNodes = computed((): { nodes: VNode[]; truncated: boolean } => {
  if (!props.content || !props.content.trim()) {
    return { nodes: [], truncated: false };
  }

  try {
    const tokens = marked.lexer(props.content) as unknown as Tokens.Generic[];
    const nodes = renderTokens(tokens);

    if (props.maxLines && props.maxLines > 0) {
      return truncateNodes(nodes, props.maxLines);
    }

    return { nodes, truncated: false };
  } catch (error) {
    logger.error('Error rendering markdown:', error);
    return {
      nodes: [h('p', { class: 'markdown-error' }, props.content)],
      truncated: false,
    };
  }
});
</script>

<template>
  <div class="markdown-content">
    <component :is="node" v-for="(node, index) in renderedNodes.nodes" :key="index" />

    <div
      v-if="showFadeOverlay && renderedNodes.truncated"
      class="markdown-fade-overlay"
      aria-hidden="true"
    />

    <span v-if="renderedNodes.truncated" class="markdown-truncated-indicator">...</span>
  </div>
</template>

<style scoped>
.markdown-content {
  position: relative;
  font-size: 14px;
  line-height: 1.6;
}

.markdown-content :deep(.markdown-heading) {
  margin: 1em 0 0.5em;
  font-weight: 600;
  color: #2c3e50;
}

.markdown-content :deep(.markdown-heading:first-child) {
  margin-top: 0;
}

.markdown-content :deep(h1) {
  font-size: 1.8em;
}

.markdown-content :deep(h2) {
  font-size: 1.5em;
}

.markdown-content :deep(h3) {
  font-size: 1.3em;
}

.markdown-content :deep(.markdown-paragraph) {
  margin: 0.8em 0;
}

/* text nodes used in list items and other tight contexts */
.markdown-content :deep(.markdown-text) {
  display: inline;
  white-space: normal;
}

.markdown-content :deep(.markdown-list) {
  padding-left: 1.5em;
  margin: 0.8em 0;
}

.markdown-content :deep(.markdown-list-item) {
  margin: 0.3em 0;
}

/* If marked emits <p> inside <li>, keep it compact */
.markdown-content :deep(.markdown-list-item > .markdown-paragraph) {
  margin: 0.2em 0;
}

.markdown-content :deep(.markdown-task-checkbox) {
  margin-right: 0.5em;
  vertical-align: middle;
}

.markdown-content :deep(.markdown-inline-code) {
  background-color: #f5f5f5;
  padding: 0.2em 0.4em;
  border-radius: 3px;
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.9em;
}

.markdown-content :deep(.markdown-code-block) {
  background-color: #f5f5f5;
  padding: 1em;
  border-radius: 4px;
  overflow-x: auto;
  margin: 1em 0;
}

.markdown-content :deep(.markdown-code-wrap) {
  position: relative;
  margin: 1em 0;
}

.markdown-content :deep(.markdown-code-wrap .markdown-code-block) {
  margin: 0;
  /* leave room so the action buttons don't overlap the first line */
  padding-top: 2.4em;
}

.markdown-content :deep(.markdown-code-actions) {
  position: absolute;
  top: 6px;
  right: 6px;
  display: flex;
  gap: 4px;
}

.markdown-content :deep(.markdown-code-action) {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid #d0d5dd;
  background: #ffffff;
  color: #374151;
  cursor: pointer;
}

.markdown-content :deep(.markdown-code-action:hover) {
  background: #eef2f7;
  color: #111827;
}

.markdown-content :deep(.markdown-code-block code) {
  background: none;
  padding: 0;
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.9em;
}

.markdown-content :deep(.markdown-blockquote) {
  border-left: 4px solid #ddd;
  margin: 1em 0;
  padding-left: 1em;
  color: #666;
  font-style: italic;
}

.markdown-content :deep(.markdown-table) {
  border-collapse: collapse;
  width: 100%;
  margin: 1em 0;
}

.markdown-content :deep(.markdown-table-header),
.markdown-content :deep(.markdown-table-cell) {
  border: 1px solid #ddd;
  padding: 0.5em;
}

.markdown-content :deep(.markdown-table-header) {
  background-color: #f5f5f5;
  font-weight: 600;
}

.markdown-content :deep(.markdown-strong) {
  color: #2c3e50;
  font-weight: 600;
}

.markdown-content :deep(.markdown-link) {
  color: #2080f0;
  text-decoration: none;
}

.markdown-content :deep(.markdown-link:hover) {
  text-decoration: underline;
}

.markdown-content :deep(.markdown-link--unsafe) {
  color: inherit;
  text-decoration: underline dotted;
  cursor: default;
}

.markdown-content :deep(.markdown-image) {
  max-width: 100%;
  height: auto;
  margin: 1em 0;
}

.markdown-content :deep(.markdown-hr) {
  border: none;
  border-top: 1px solid #ddd;
  margin: 1em 0;
}

.markdown-truncated-indicator {
  color: #999;
  font-style: italic;
}

.markdown-fade-overlay {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 2.5em;
  pointer-events: none;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1));
}
</style>
